import { AxiosResponse } from 'axios';
import { reportType } from '../../../../infrastructure/constants/reportType';
import clear from '../../../../infrastructure/helpers/common/array/clear';
import CustomDataSourceFields from '../../../../types/report/CustomDataSourceFields';
import dataSourceKind from '../../../../infrastructure/constants/dataSourceKind';
import httpClient from '../../../../infrastructure/helpers/common/httpClient';
import IngestionConfigOption from '../../../../types/ingestion/IngestionConfigOption';
import ReportConfig from '../../../../types/report/ReportConfig';
import reportConfigCountHelper from '../../../../infrastructure/helpers/report/reportConfigCountHelper';
import ReportConfigCounts from '../../../../types/report/ReportConfigCounts';
import ReportFieldDefinition from '../../../../types/report/ReportFieldDefinition';
import ReportPageContext from './ReportPageContext';
import ReportRegimeDetails from '../../../../types/report/ReportRegimeDetails';
import typeHelper from '../../../../infrastructure/helpers/common/typeHelper';
import urlHelper from '../../../../infrastructure/helpers/common/urlHelper';
import Operand from '../../../../types/functions/Operand/Operand';
import MatchingKey from '../../../../types/report/MatchingKey';
import GlobalModuleConfig from '../../../../types/report/GlobalModuleConfig';
import Mode from '../../../../types/report/Mode';
import IngestionConfig from '../../../../types/ingestion/IngestionConfig';
import FieldDefinition from '../../../../types/common/FieldDefinition';
import Component from '../../../../types/common/Component';
import copyArray from '../../../../infrastructure/helpers/common/copyArray';
import RefDataSource from '../../../../types/report/RefDataSource';

const getDataSourceOptions = (reportType: string, reportConfigId?: number): Promise<AxiosResponse<IngestionConfigOption[]>> => {

    let route = '';

    if (reportConfigId) {
        route = urlHelper.buildRoute(['ingestion', reportConfigId, 'enabled', reportType]);
    }
    else {
        route = urlHelper.buildRoute(['ingestion', 'enabled', reportType]);
    }

    return httpClient.get<IngestionConfigOption[]>(route);
};

const getReportDataSources = (reportConfig: ReportConfig): Promise<AxiosResponse<IngestionConfig[]>> => {

    let ids = reportConfig.refDataSources.map(x => x.id);

    if (reportConfig.type !== reportType.accuracy && reportConfig.type !== reportType.globalModule) {

        ids.push(reportConfig.rawDataSourceId as number);
    }

    let url = urlHelper.buildCollectionParamUrl('ingestion', 'ids', ids);

    return httpClient.get<IngestionConfig[]>(url);
};

const getRegimeDetails = (reportConfig: ReportConfig): Promise<AxiosResponse<ReportRegimeDetails>> => {

    let url = urlHelper.buildUrl('report-field/details', { regime: reportConfig.regime });

    return httpClient.get<ReportRegimeDetails>(url);
};

const getReportFields = (reportConfig: ReportConfig): Promise<AxiosResponse<ReportFieldDefinition[]>> => {

    let url = urlHelper.buildUrl('report-field', { regime: reportConfig.regime });

    return httpClient.get<ReportFieldDefinition[]>(url);
};



const prepareRefDataSourcesForConfig = (globalRefDataSources: IngestionConfig[], dataSources: IngestionConfig[], missingDataSources: Map<number, string>, reportConfig: ReportConfig): RefDataSource[] => {

    let refDataSources = copyArray(reportConfig.refDataSources);

    missingDataSources.forEach((value, key) => {

        let ds = globalRefDataSources.find(x => x.id === key);

        if (ds?.id !== reportConfig.rawDataSourceId) {

            refDataSources.push({ id: ds?.id as number, dataSouceKind: ds?.dataSourceKind, fieldDefinitions: ds?.fieldDefinitions, name: ds?.name } as RefDataSource);
        }
    });

    refDataSources.forEach((ds) => {

        if (!ds.fieldDefinitions) {

            ds.fieldDefinitions = dataSources.find(x => x.id === ds.id)?.fieldDefinitions as FieldDefinition[];
        }
    });

    return refDataSources;
}

const getCustomDataSourceFields = (): Promise<AxiosResponse<CustomDataSourceFields>> => {

    return httpClient.get<CustomDataSourceFields>('custom-data-source/fields');
};

const getConfigById = (id: number, released: boolean): Promise<AxiosResponse<ReportConfig>> => {

    let url = 'report/' + id;

    if (released) {

        url += '/released';
    }

    return httpClient.get<ReportConfig>(url);
};

const getCounts = (regime: string, reportId: number | null): Promise<AxiosResponse<ReportConfigCounts>> => {

    let parts = ['report', regime, 'counts'];

    if (typeHelper.isNumber(reportId)) {

        parts.push((reportId as number).toString());
    }

    let url = urlHelper.buildRoute(parts);

    return httpClient.get<ReportConfigCounts>(url);
};

const seedReportDataSourceOptions = (dataSourceOptions: IngestionConfigOption[], context: ReportPageContext): void => {

    dataSourceOptions.forEach(x => context.collections.dataSourceOptions.push(x));
};

const seedReportDataSources = (dataSources: IngestionConfig[], context: ReportPageContext): void => {

    clear(context.collections.dataSources);

    dataSources.filter(x => x.dataSourceKind !== dataSourceKind.accuracy && x.dataSourceKind !== dataSourceKind.globalRef).forEach(x => context.collections.dataSources.push(x));
    dataSources.filter(x => x.dataSourceKind === dataSourceKind.accuracy).forEach(x => context.collections.dataSources.push(x));
    dataSources.filter(x => x.dataSourceKind === dataSourceKind.globalRef).forEach(x => context.collections.dataSources.push(x));
};

const seedReportFields = (fieldDefinitions: ReportFieldDefinition[], context: ReportPageContext): void => {

    fieldDefinitions.forEach(x => context.collections.reportFields.push(x));
};

const addGlobalModuleConfig = async (context: ReportPageContext, configCreatedById: string): Promise<void> => {

    let res = await httpClient.get<GlobalModuleConfig>(`report/global-module/${configCreatedById}`);

    if (res.data.userDefinedFunctions) {
        res.data.userDefinedFunctions.forEach(f => f.mode = Mode.Global);

        context.collections.globalModuleConfig = res.data;
    }
}

const addGlobalModuleDataSources = (config: GlobalModuleConfig, context: ReportPageContext) => {

    config.refDataSources.forEach((ds) => {
        context.collections.globalModuleDataSources.push({
            id: ds.id,
            name: ds.name as string,
            fieldDefinitions: ds.fieldDefinitions as FieldDefinition[],
            dataSourceKind: ds.dataSouceKind as string
        } as IngestionConfig)
    });
}

const getGlobalComponentReferences = (configId: number): Promise<AxiosResponse<Component[]>> => {

    return httpClient.get<Component[]>(`report/global-module/references/${configId}`);
}

const seedCustomDataSourceFields = (customDataSourceFields: CustomDataSourceFields, context: ReportPageContext): void => {

    customDataSourceFields.firds.forEach(x => context.collections.customDataSourceFields.firds.push(x));
    customDataSourceFields.leiLevel1.forEach(x => context.collections.customDataSourceFields.leiLevel1.push(x));
    customDataSourceFields.exchangeRates.forEach(x => context.collections.customDataSourceFields.exchangeRates.push(x));
    customDataSourceFields.cnProduct.forEach(x => context.collections.customDataSourceFields.cnProduct.push(x));
};

const getEmptyReportPageContext = (type?: string): ReportPageContext => {
    return {
        collections: {
            globalModuleReferences: [],
            dataSourceOptions: [],
            dataSources: [],
            reportFields: [],
            globalModuleConfig: getEmptyGlobalModuleConfig(),
            customDataSourceFields: {
                firds: [],
                leiLevel1: [],
                cnProduct: [],
                exchangeRates: [],
                annaDsbUpiEnrichment: [],
                annaDsbUpi: []
            },
            globalModuleDataSources: []
        },
        counts: reportConfigCountHelper.getEmpty(),
        config: getEmptyReportConfig(type),
    };
};

const isPendingApproval = async (reportConfigId: number): Promise<boolean> => {

    let route = urlHelper.buildRoute(['report-version', reportConfigId, 'is-pending-approval']);

    var result = await httpClient.getWithAsyncAwait(route);

    return result.data as boolean;
};

const getEmptyGlobalModuleConfig = (type?: string): GlobalModuleConfig => {
    return {
        id: 0,
        name: '',
        userDefinedFunctions: [],
        lookups: [],
        variables: [],
        refDataSources: []
    };
}

const getEmptyReportConfig = (type?: string): ReportConfig => {
    return {
        id: null,
        createdById: '',
        name: '',
        description: '',
        regime: '',
        subRegime: null,
        type: type || reportType.completenessFieldLevelMatching,
        rawDataSourceId: null,
        refDataSources: [],
        matchingKey: {
            operands: [] as Operand[]
        } as MatchingKey,
        matchingKeyEndPoint: {
            operands: [] as Operand[]
        } as MatchingKey,
        isinSelectors: [],
        leiSelectors: [],
        exchangeRatesSelectors: [],
        annaDsbUpiEnrichmentSelectors: [],
        annaDsbUpiSelectors: [],
        symbolSelectors: [],
        lseSelectors: [],
        firdsFcaInstrumentSelectors: [],
        firdsEsmaInstrumentSelectors: [],
        fcaRegulatedEntitiesSelectors: [],
        userDefinedFunctions: [],
        lookups: [],
        dictionaries: [],
        variables: [],
        aggregations: [],
        filters: [],
        endpointFilters: [],
        cases: [],
        validations: [],
        optionalModules: [],
        isReleased: false,
        version: null as any
    };
};

export {
    getDataSourceOptions,
    getReportDataSources,
    getRegimeDetails,
    getReportFields,
    getCustomDataSourceFields,
    getConfigById,
    getCounts,
    seedReportDataSourceOptions,
    seedReportDataSources,
    seedReportFields,
    seedCustomDataSourceFields,
    getEmptyReportConfig,
    getEmptyReportPageContext,
    isPendingApproval,
    getEmptyGlobalModuleConfig,
    addGlobalModuleConfig,
    addGlobalModuleDataSources,
    getGlobalComponentReferences,
    prepareRefDataSourcesForConfig
};
